import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { UserInfo, UserService } from '../../services/user/user.service';
import { AwardProgramInfo, AwardsService } from '../../services/awards/awards.service';
import { ApplicationService, JudgingForm, NomineeTypeInfo, RubricSectionEntry, JudgeSaveForm, JudgeSaveFormData } from '../../services/application/application.service';
import { ErrorService } from '../../services/error/error.service';
import { ScrollService } from '../../services/scroll/scroll.service';
import { SplashService } from '../../services/splash/splash.service';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
    selector: 'app-judgeform',
    templateUrl: './judgeform.component.html',
    styleUrls: ['./judgeform.component.scss']
})

export class JudgeFormComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();
    User: UserInfo;
    Award: AwardProgramInfo;
    ViewReady: boolean;
    RatingForm: FormGroup;
    JudgingForm: JudgingForm;
    NomType: NomineeTypeInfo;
    Phase: string;
    ChangesDetected: boolean;
	ActionExecuting: boolean;
	SchoolYear: string;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private errSvc: ErrorService,
        private fb: FormBuilder,
        private scrollSvc: ScrollService,
        private splshSvc: SplashService,
        private usrSvc: UserService,
        private route: ActivatedRoute,
		private router: Router,
		private settingsSvc: SettingsService) {
    }

    ngOnInit() {
        this.ViewReady = false;
        this.ChangesDetected = false;
        this.ActionExecuting = false;
        this.User = this.usrSvc.User;
		this.Award = this.awardSvc.CurrentAwards;

		if (this.User == null) return;

        this.RatingForm = this.fb.group({
            RatingsList: this.fb.array([])
        });

        this.route.params.pipe(takeUntil(this._subrelease)).subscribe(params => {
            let nomUID = params['uid'];
            let impJudgeID = params['id'];
			let phase = params['phase'];

			this.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(this._subrelease)).subscribe(year => {
				this.SchoolYear = year;

				let nomTypes$ = this.appSvc.GetNomineeTypes();
				let judgeForm$ = this.appSvc.GetJudgingForm(this.User.AuthToken, this.User.CurrentRole, nomUID, phase, impJudgeID, year);

				combineLatest(nomTypes$, judgeForm$).pipe(
					takeUntil(this._subrelease),
					map(_pck => {
						return {
							nomTypes: _pck[0],
							judgeForm: _pck[1]
						};
					})
				).subscribe(packet => {
					this.JudgingForm = packet.judgeForm;
					this.NomType = packet.nomTypes.find(f => f.NomineeTypeID === this.JudgingForm.NomineeTypeID);

					this.JudgingForm.SelectedTabIndex = 0;

					switch (this.JudgingForm.JudgingPhase) {
						case 1: {
							this.Phase = 'Initial Judging';
							break;
						}
						case 2: {
							this.Phase = 'Initial Judging Tie-Breaker';
							break;
						}
						case 3: {
							this.Phase = 'Final Judging';
							break;
						}
						case 4: {
							this.Phase = 'Final Judging Tie-Breaker';
							break;
						}
						default: {
							this.Phase = '';
							break;
						}
					}

					this.SetRubricQuestionsScored();

					//create array of formGroups
					let ratingsArray = this.RatingForm.get('RatingsList') as FormArray;
					let score: number;
					for (let sec of this.JudgingForm.Sections) {
						for (let rubricQ of sec.RubricQuestions) {
							if (this.JudgingForm.Details != null) {
								let detail = this.JudgingForm.Details.find(f => f.JudgeRubricDetailID === rubricQ.RubricDetailID);
								score = detail == null ? null : detail.UnweightedScore;
							}
							ratingsArray.push(this.CreateRatingDetailFormGroup(rubricQ, score));
						}

						for (let formQ of sec.Questions) {
							if (formQ.HasOptions) {
								let displayList: string[] = [];
								let idList: number[] = formQ.Response.indexOf('|') < 0 ? [+formQ.Response] : formQ.Response.split('|').map(x => +x);

								for (let o of formQ.Options) {
									if (idList.includes(o.Value)) {
										displayList.push(o.DisplayText);
									}
								}

								formQ.Response = displayList.join(', ');
							}
						}
					}

					ratingsArray.valueChanges.pipe(takeUntil(this._subrelease)).subscribe((values: any[]) => {
						let scoredRubricQs = values.filter(v => v.Score != null);

						//update RubricQuestionsScored
						for (let sec of this.JudgingForm.Sections) {
							sec.RubricQuestionsScored = 0;

							for (let q of sec.RubricQuestions) {
								if (scoredRubricQs.some(v => v.RubricDetailID === q.RubricDetailID)) {
									sec.RubricQuestionsScored++;
								}
							}
						}

						//update ChangesDetected

						//new scoresheet
						if (this.JudgingForm.Details == null) {
							this.ChangesDetected = true;
						}
						//# of questions scored changes
						else
							if (this.JudgingForm.Details.length !== scoredRubricQs.length) {
								this.ChangesDetected = true;
							}
							//values of already answered questions changes
							else {
								this.ChangesDetected = false;

								for (let v of scoredRubricQs) {
									let d = this.JudgingForm.Details.find(f => f.JudgeRubricDetailID === v.RubricDetailID);
									if (v.Score !== d.Score) {
										this.ChangesDetected = true;
									}
								}
							}
					}, error => this.errSvc.HandleServiceError(error));

					this.ViewReady = true;
				}, error => this.errSvc.HandleServiceError(error));
			});
        }, error => this.errSvc.HandleServiceError(error));
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    Back() {
        this.router.navigate([this.Award.URLPath + '/judgenominees']);
    }

    ChangeTab(index: number) {
        this.JudgingForm.SelectedTabIndex = index;

        // Trigger the scroll event so the master container can scroll the panel back up to view.
        this.scrollSvc.TriggerScroll();
    }

    CreateRatingDetailFormGroup(detail: RubricSectionEntry, score: number): FormGroup {
        return this.fb.group({
            RubricDetailID: detail.RubricDetailID,
            Text: detail.QuestionText,
            Score: score
        });
    }

    GetRubricIndex(secIndex: number, questionIndex: number): number {
        let index = 0;

        for (let s = 0; s < secIndex; s++) {
            index = index + this.JudgingForm.Sections[s].RubricQuestions.length;
        }

        index = index + questionIndex;
        return index;
    }

    SaveRatings(NextSection: boolean) {
        if (this.ActionExecuting === true) {
            return;
        }

        this.ActionExecuting = true;
        let scores: JudgeSaveFormData[] = [];
        let newSectionIndex: number = this.JudgingForm.SelectedTabIndex;

        if (NextSection && this.JudgingForm.SelectedTabIndex !== (this.JudgingForm.Sections.length - 1)) {
            newSectionIndex++;
        }

        for (let s of this.RatingForm.value.RatingsList) {
            let score: JudgeSaveFormData = {
                RubricDetailID: s.RubricDetailID,
                Score: s.Score
            };
            scores.push(score);
        }

        let saveForm: JudgeSaveForm = {
            NominationFormUID: this.JudgingForm.NominationFormUID,
            NominationID: this.JudgingForm.NominationID,
            JudgeID: this.JudgingForm.JudgeID,
            JudgeRatingUID: this.JudgingForm.JudgeRatingUID,
            JudgeRatingVersion: this.JudgingForm.JudgeRatingVersion,
            JudgingPhase: this.JudgingForm.JudgingPhase,
            Finalized: this.JudgingForm.Finalized,
            FormData: scores
        };

		this.appSvc.SaveJudgingForm(this.User.AuthToken, this.User.CurrentRole, saveForm, this.SchoolYear).pipe(takeUntil(this._subrelease)).subscribe(form => {
            this.splshSvc.CloseSplash();
            this.splshSvc.SnackbarSuccess(`Submit successful`, 10000);
            this.JudgingForm = form;
            this.ChangesDetected = false;
            this.SetRubricQuestionsScored();
            this.JudgingForm.SelectedTabIndex = newSectionIndex;
            this.ActionExecuting = false;
        }, error => {
            this.splshSvc.CloseSplash();
            this.splshSvc.SnackbarError(error.error, 10000);
            this.ActionExecuting = false;
        });
    }

    SetRubricQuestionsScored() {
        for (let sec of this.JudgingForm.Sections) {
            sec.RubricQuestionsScored = 0;
        }

        if (this.JudgingForm.Details != null) {
            for (let d of this.JudgingForm.Details) {
                this.JudgingForm.Sections.find(s => s.SectionID === d.FormTemplateSectionID).RubricQuestionsScored++;
            }
        }
    }

}
